import { PrimeStakingQueueAbi } from '../abis/primestaking'
import type { LstWithdrawalRequest } from '../types'
import { toBigInt, toNumber, type LstWithdrawalReader } from './shared'

/**
 * PrimeStaking psXDC — withdrawal enumeration on the V3 vault's global
 * FIFO queue.
 *
 * `redeemWithQueue(shares, receiver)` mints a GLOBAL `requestId`; there
 * is no per-user getter, so — exactly like the iBERA reader — we scan
 * `withdrawalRequests(id)` over the most recent window and filter by the
 * `receiver` field. A request is "open" while `assets > 0`; claimability
 * is taken from the per-request `processed` flag (equivalently
 * `id < nextUnprocessedWithdrawalRequestId()`), since once the masternode
 * payout settles a request its assets become claimable via
 * `claimQueuedAssets(receiver)`. There is no fixed cooldown, so no
 * `readyAt` is set — pending requests surface with `status: 'pending'`.
 *
 * Stage 1: `nextWithdrawalRequestId()` (exclusive id upper bound) +
 *          `nextUnprocessedWithdrawalRequestId()` (finalisation frontier).
 * Stage 2: `withdrawalRequests(i)` over the most recent `MAX_SCAN` ids.
 *
 * The queue is global and unbounded; the scan is capped at the tail
 * (open requests cluster there — claimed ones at the front are settled),
 * matching the "we may not know all your requests" tolerance.
 */
const MAX_SCAN = 3000

export const readerPrimeStaking: LstWithdrawalReader = {
  fetch: async (user, multicallRetry, chainId, entry) => {
    const vault = entry.withdrawalContract
    if (!vault) return []

    let stage1: unknown[]
    try {
      stage1 = (await multicallRetry({
        chain: chainId,
        calls: [
          { address: vault, name: 'nextWithdrawalRequestId', params: [] },
          {
            address: vault,
            name: 'nextUnprocessedWithdrawalRequestId',
            params: [],
          },
        ] as unknown as never[],
        abi: [PrimeStakingQueueAbi, PrimeStakingQueueAbi] as unknown as never,
      })) as unknown[]
    } catch {
      return []
    }
    const length = toNumber(stage1[0]) ?? 0
    const frontier = toBigInt(stage1[1]) ?? 0n
    if (length === 0) return []

    const start = length > MAX_SCAN ? length - MAX_SCAN : 0
    const ids = Array.from({ length: length - start }, (_, k) => start + k)

    let stage2: unknown[]
    try {
      stage2 = (await multicallRetry({
        chain: chainId,
        calls: ids.map((i) => ({
          address: vault,
          name: 'withdrawalRequests',
          params: [BigInt(i)],
        })) as unknown as never[],
        abi: ids.map(() => PrimeStakingQueueAbi) as unknown as never,
      })) as unknown[]
    } catch {
      return []
    }

    const target = user.toLowerCase()
    const out: LstWithdrawalRequest[] = []
    for (let k = 0; k < ids.length; k++) {
      const r = stage2[k] as
        | { receiver?: unknown; assets?: unknown; processed?: unknown }
        | unknown[]
        | undefined
      if (!r) continue
      const receiver = Array.isArray(r) ? r[1] : r.receiver
      const assets = Array.isArray(r) ? toBigInt(r[3]) : toBigInt(r.assets)
      const processed = Array.isArray(r) ? r[4] : r.processed
      if (
        typeof receiver !== 'string' ||
        receiver.toLowerCase() !== target ||
        assets === undefined ||
        assets === 0n
      ) {
        continue
      }
      const id = BigInt(ids[k])
      const claimable = processed === true || id < frontier
      out.push({
        lst: entry.lst,
        brand: entry.brand,
        symbol: entry.symbol,
        requestId: String(ids[k]),
        amountUnderlying: assets.toString(),
        status: claimable ? 'claimable' : 'pending',
      })
    }
    return out
  },
}
